if (!customElements.get('cfs-slideshow')) {
  class CfSlideshow extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this.slides = Array.from(this.querySelectorAll('.cfs-slide'));
      this.originalTotal = this.slides.length;
      if (this.originalTotal === 0) return;

      const track = this.querySelector('.cfs-track');
      if (this.originalTotal <= 4) {
        this.slides.forEach(slide => {
          const clone = slide.cloneNode(true);
          track.appendChild(clone);
        });
        this.slides = Array.from(this.querySelectorAll('.cfs-slide'));
      }
      
      this.dots = Array.from(this.querySelectorAll('.cfs-dot'));
      this.currentIndex = 0;
      this.total = this.slides.length;

      this.updatePositions(false);
      this.updateDots();

      const prevBtn = this.querySelector('.cfs-nav__btn--prev');
      const nextBtn = this.querySelector('.cfs-nav__btn--next');
      if (prevBtn) prevBtn.addEventListener('click', () => this.prev());
      if (nextBtn) nextBtn.addEventListener('click', () => this.next());

      this.dots.forEach((dot, i) => {
        dot.addEventListener('click', () => this.goTo(i));
      });

      // Click side slides to navigate (and prevent click if dragged)
      this.addEventListener('click', (e) => {
        if (this._hasMoved) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        const slide = e.target.closest('.cfs-slide');
        if (!slide) return;
        const pos = parseInt(slide.dataset.pos);
        if (pos === -1) { e.preventDefault(); this.prev(); }
        else if (pos === 1) { e.preventDefault(); this.next(); }
      }, true); // Capture phase

      // Unified Drag / Swipe
      this._isDragging = false;
      this._startX = 0;
      this._hasMoved = false;

      const onDragStart = (e) => {
        if (e.type === 'mousedown' && e.button !== 0) return;
        this._isDragging = true;
        this._hasMoved = false;
        this._startX = e.type.includes('mouse') ? e.clientX : e.touches[0].clientX;
        this.style.cursor = 'grabbing';
      };

      const onDragMove = (e) => {
        if (!this._isDragging) return;
        const currentX = e.type.includes('mouse') ? e.clientX : e.touches[0].clientX;
        if (Math.abs(currentX - this._startX) > 10) {
          this._hasMoved = true;
        }
      };

      const onDragEnd = (e) => {
        if (!this._isDragging) return;
        this._isDragging = false;
        this.style.cursor = '';
        const currentX = e.type.includes('mouse') ? e.clientX : (e.changedTouches ? e.changedTouches[0].clientX : this._startX);
        const dx = currentX - this._startX;
        
        if (Math.abs(dx) > 50) {
          dx < 0 ? this.next() : this.prev();
        }
        
        // Reset hasMoved after a short delay so click event can be blocked
        setTimeout(() => { this._hasMoved = false; }, 50);
      };

      this.addEventListener('mousedown', onDragStart);
      this.addEventListener('mousemove', onDragMove);
      window.addEventListener('mouseup', onDragEnd);
      
      this.addEventListener('touchstart', onDragStart, { passive: true });
      this.addEventListener('touchmove', onDragMove, { passive: true });
      this.addEventListener('touchend', onDragEnd, { passive: true });

      if (this.dataset.autoplay === 'true') {
        this.startAutoplay();

        // Pause on hover
        this.addEventListener('mouseenter', () => this.stopAutoplay());
        this.addEventListener('mouseleave', () => this.startAutoplay());

        // Pause when off-screen
        if ('IntersectionObserver' in window) {
          new IntersectionObserver((entries) => {
            entries[0].isIntersecting ? this.startAutoplay() : this.stopAutoplay();
          }).observe(this);
        }
      }
    }

    /**
     * Compute each slide's position relative to currentIndex and set data-pos.
     * @param {boolean} animate - whether to allow CSS transitions
     */
    updatePositions(animate = true) {
      this.slides.forEach((slide, i) => {
        let pos = i - this.currentIndex;

        // Normalize for wrap-around
        if (pos > this.total / 2) pos -= this.total;
        if (pos < -this.total / 2) pos += this.total;

        // Clamp to buffer range
        const clamped = Math.max(-2, Math.min(2, pos));

        const oldPos = parseInt(slide.dataset.pos ?? '99');

        // Teleport invisible slides that would cross sides (no visual jump)
        const wasHidden = Math.abs(oldPos) >= 2 || isNaN(oldPos);
        const willBeHidden = Math.abs(clamped) >= 2;
        const sideChange = Math.sign(oldPos) !== Math.sign(clamped);

        if (!animate || (wasHidden && willBeHidden && sideChange)) {
          slide.style.transition = 'none';
          slide.dataset.pos = clamped;
          void slide.offsetWidth; // force reflow
          slide.style.transition = '';
        } else {
          slide.dataset.pos = clamped;
        }
      });
    }

    updateDots() {
      const activeDotIndex = this.currentIndex % this.originalTotal;
      this.dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === activeDotIndex);
      });
    }

    goTo(index) {
      if (index === (this.currentIndex % this.originalTotal)) return;
      
      let target = index;
      let currentMod = this.currentIndex % this.originalTotal;
      let diff = target - currentMod;
      
      if (diff > this.originalTotal / 2) diff -= this.originalTotal;
      if (diff < -this.originalTotal / 2) diff += this.originalTotal;
      
      this.currentIndex = ((this.currentIndex + diff) % this.total + this.total) % this.total;
      this.updatePositions();
      this.updateDots();
      this.resetAutoplay();
    }

    next() { this.goTo((this.currentIndex + 1) % this.total); }
    prev() { this.goTo((this.currentIndex - 1 + this.total) % this.total); }

    startAutoplay() {
      if (this.autoplayTimer) return;
      const speed = (parseInt(this.dataset.speed) || 5) * 1000;
      this.autoplayTimer = setInterval(() => this.next(), speed);
    }

    stopAutoplay() {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    resetAutoplay() {
      if (this.dataset.autoplay === 'true') {
        this.stopAutoplay();
        this.startAutoplay();
      }
    }
  }

  customElements.define('cfs-slideshow', CfSlideshow);
}
